import { Injectable } from '@angular/core';
import type { AdditionalInfoFieldKey } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { TeethManagerService } from '@modules/tooth-editor/state/teeth-manager-service';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { RxIntuitiveIndicationMapperService } from '../rx-intuitive-indication-mapper.service';
import { RxIntuitiveTeethDiagramStore } from '../../state/rx-intuitive-teeth-diagram-store';

/**
 * Owns all write / mutation operations for the tooth editor.
 *
 * Both {@link RxIntuitiveToothEditorFacade} and
 * {@link RxIntuitiveToothEditorBridgeFacade} compose this service so that
 * store mutations are not duplicated across the two thin facades.
 */
@Injectable()
export class RxIntuitiveToothEditorMutationService {
	constructor(
		private readonly teethDiagramStore: TeethDiagramStore,
		private readonly intuitiveStore: RxIntuitiveTeethDiagramStore,
		private readonly teethManagerService: TeethManagerService,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService
	) {}

	/**
	 * Resets the given teeth to the default unit type and marks them as
	 * recently edited.  Does nothing when the array is empty.
	 */
	resetIndicationForTeeth(toothIdsToReset: number[]): void {
		if (toothIdsToReset.length === 0) {
			return;
		}

		const defaultUnitType = this.indicationMapper.getDefaultUnitType();

		// Full reset (not only UnitTypeID) so restorative fields do not linger
		// on Regular teeth after a copy-from operation.
		this.teethDiagramStore.resetIndicationByIds(toothIdsToReset, defaultUnitType);
		this.markRecentlyEditedTeeth(toothIdsToReset);
	}

	/**
	 * Persists crown form fields (material, shade system, shade body,
	 * specification) for the specified teeth and marks them as recently edited.
	 */
	updateToothCrownData(crownForm: CrownForm): void {
		this.teethDiagramStore.updateTeethByIds(crownForm.teethIds, {
			MaterialID: crownForm.material,
			ShadeSystemId: crownForm.shadeSystem,
			ShadeBody: crownForm.shadeBody,
			SpecificationId: crownForm.specification
		});
		this.markRecentlyEditedTeeth(crownForm.teethIds);
	}

	/**
	 * Applies crown additional info values to the given teeth, marks them as
	 * recently edited, and notifies the teeth-manager of any cleared controls.
	 */
	applyToothAdditionalInfo(toothIds: number[], result: AdditionalInfoResult): void {
		this.teethDiagramStore.updateTeethByIds(toothIds, result.values);
		this.markRecentlyEditedTeeth(toothIds);
		this.markCrownControlsAsRemovedByUser(toothIds, result.clearedFields);
	}

	/**
	 * Clears the disabled crown fields for the specified teeth.
	 * Does NOT mark teeth as recently edited (no indication-level change).
	 */
	clearDisabledToothFields(toothIds: number[], payload: Partial<Record<string, unknown>>): void {
		this.teethDiagramStore.updateTeethByIds(toothIds, payload);
	}

	/**
	 * Marks the given teeth as recently edited in the intuitive diagram store
	 * (updates `toothLastTouchedAt` timestamps).
	 */
	markRecentlyEditedTeeth(toothIds: number[]): void {
		this.intuitiveStore.markRecentlyEditedTeeth(toothIds);
	}

	/**
	 * Notifies the teeth-manager service that crown controls were explicitly
	 * removed by the user for the given teeth / fields combination.
	 * No-op when `clearedFields` is empty.
	 */
	markCrownControlsAsRemovedByUser(teethIds: number[], clearedFields: AdditionalInfoFieldKey[]): void {
		if (clearedFields.length === 0) {
			return;
		}

		teethIds.forEach(id => {
			this.teethManagerService.markCrownControlsAsRemovedByUser({ ToothID: id }, clearedFields);
		});
	}

	/**
	 * Resets the indication for the given teeth and then applies a partial
	 * tooth update from a modal save action.  Used after implant-position /
	 * implant-based modals close with a save result.
	 */
	updateTeethByIdsForIndicationWithModal(
		teethIds: number[],
		partialToothUpdate: Partial<Omit<Tooth, 'ToothID'>>,
		unitType: number
	): void {
		this.teethDiagramStore.resetIndicationByIds(teethIds, unitType);
		this.teethDiagramStore.updateTeethByIds(teethIds, partialToothUpdate);
		this.markRecentlyEditedTeeth(teethIds);
	}
}
